// New York University: the template college page (#2579), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - NYU's Common Data Set 2025-2026, the July 2026 file that includes
//    section G (C1 applicants/admits/enrollees for Fall 2025; C8 test policy
//    for Fall 2027; C14/C21 dates; G1/G5 2026-2027 costs). The PDF's running
//    header still reads "Common Data Set 2024-2025"; its folder, its file name
//    and its own contents (Fall 2025 counts, 2026-2027 costs) are 2025-2026.
//  - nyu.edu admissions pages (first-year deadlines, early decision,
//    standardized tests, additional program requirements), read in a browser
//    because nyu.edu blocks scripted fetches.
//  - College Scorecard API, school id 193900 (net price by family income).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'NYU Common Data Set 2025-2026',
        url: 'https://www.nyu.edu/content/dam/nyu/institutionalResearch/documents/cds-2025-2026/CDS%2025-26%20Final%20July%202026%20(including%20G%20July_2026).pdf',
        read: '2026-09-30',
    },
    firstYear: {
        label: 'NYU Admissions, First-Year Applicants',
        url: 'https://www.nyu.edu/admissions/undergraduate-admissions/how-to-apply/all-freshmen-applicants.html',
        read: '2026-09-30',
    },
    earlyDecision: {
        label: 'NYU Admissions, Early Decision',
        url: 'https://www.nyu.edu/admissions/undergraduate-admissions/how-to-apply/all-freshmen-applicants/early-decision.html',
        read: '2026-09-30',
    },
    tests: {
        label: 'NYU Admissions, Standardized Tests',
        url: 'https://www.nyu.edu/admissions/undergraduate-admissions/how-to-apply/standardized-tests.html',
        read: '2026-09-30',
    },
    programs: {
        label: 'NYU Admissions, Additional Program Requirements',
        url: 'https://www.nyu.edu/admissions/undergraduate-admissions/how-to-apply/all-freshmen-applicants/additional-program-requirements.html',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?193900-New-York-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'new-york-university',
    kbId: 'new_york_university',
    name: 'New York University',
    shortName: 'NYU',
    location: 'New York, NY',
    control: 'Private',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (NYU brand guidelines); decoration only.
    branding: {
        primary: '#57068C',
        secondary: '#8900E1',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/new_york_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 114125,
        admitted: 10340,
        enrolled: 5662,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'NYU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'The Common Data Set leaves its early decision counts blank, so it gives no early decision acceptance rate.',
        source: 'cds',
        where: 'section C21',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to one of NYU\'s schools. Some ask for more than the Common App:',
        rows: [
            { school: 'Tisch School of the Arts', requirement: 'An artistic review for every program: a portfolio or an audition.' },
            { school: 'Steinhardt, Music', requirement: 'An audition, portfolio or supplemental application for every Music program.' },
            { school: 'Steinhardt, Studio Art', requirement: 'A portfolio with an artist statement, sent to the art department.' },
            { school: 'Tandon School of Engineering', requirement: 'Competitive applicants have physics, chemistry and calculus (or at least precalculus) on their transcript.' },
            { school: 'Stern School of Business', requirement: 'Applicants should have calculus, or at least precalculus, on their transcript.' },
        ],
        note: 'For a program with an audition or portfolio, NYU strongly recommends sending the application a month before the deadline.',
        source: 'programs',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'December 15', binding: true },
            { plan: 'Early Decision II', deadline: 'January 1', decision: 'February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'April 1', binding: false },
        ],
        note: 'Applications close at 11:59 pm in your local time zone.',
        source: 'firstYear',
        binding: 'Early decision is binding: if you are admitted, you are expected to enroll and withdraw your other applications. NYU makes exceptions when you are not admitted to your first-choice NYU school, program or campus, or when the aid offered does not meet enough of your need. If you are not admitted early, you cannot apply again in regular decision.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'NYU stays test-optional through the 2027-2028 application cycle. It is not test-blind: if you send scores, they are read. You choose one form of testing: the SAT, the ACT, AP exams, the IB Diploma or A-Levels.',
        source: 'tests',
        cdsLine: 'The Common Data Set marks the SAT or ACT as recommended, not required, for fall 2027 applicants, with scores due by January 31.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [
            { topic: 'Testing', line: 'NYU stays test-optional for fall 2028 entry: its policy runs through the 2027-28 application cycle.', source: 'tests' },
        ],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition and fees', amount: 68574 },
            { item: 'Food and housing (on campus, double room)', amount: 25250 },
            { item: 'Books and supplies', amount: 1492 },
            { item: 'Transportation', amount: 2438 },
            { item: 'Other expenses', amount: 2054 },
        ],
        total: 99808,
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 35737,
            byIncome: [
                { income: '$0 to $30,000', amount: 16349 },
                { income: '$30,001 to $48,000', amount: 15400 },
                { income: '$48,001 to $75,000', amount: 21188 },
                { income: '$75,001 to $110,000', amount: 31450 },
                { income: 'Over $110,000', amount: 68303 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from NYU\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
